import { Fragment, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Download, ChevronRight, ChevronDown, ArrowUpRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiFetch } from '@/lib/api';
import { useRootLabel, useScopeStore, findNodeInTree, type NodesResponse } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { KpiCard } from '@/components/KpiCard';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/hooks/useTableSort';
import { formatCurrency, formatPercent, formatCount, formatVariance } from '@/lib/format';
import { formatCountedMonths, resolveMonthTargetCard } from '@/lib/kpi-target-scope';
import { fiscalPositionOfMonth } from '@/lib/fiscal-year';
import { visitorCountSourceBadge } from '@/lib/visitor-count-labels';
import { downloadAsCsv } from '@/lib/csv-export';
import { PublishGateBanner } from '@/components/publish/PublishGateBanner';
import { AiPeriodCommentPanel } from '@/components/publish/AiPeriodCommentPanel';
import type { PublishGateInfo } from '@/lib/types-publish-gate';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

/** kpi_threshold_master の kpi_type をキーとした閾値+警告フラグ。DB の enum 値をそのままキーに使う
 *  (例: 'cost_rate', 'gross_margin', 'labor_rate', 'opex_rate', 'sales', 'customer_count', 'avg_price') */
type ThresholdEntry = { value: number; direction: 'above' | 'below'; alert: boolean };

type StoreKpi = {
  store_id: string;
  store_name: string;
  sales_tax_excluded: number;
  // v0.5⑤ (困りごと③): サーバは既に算出済みだった内訳 (routes/dashboard.ts) を型に反映しただけ
  store_sales_tax_excluded: number;
  delivery_sales_tax_excluded: number;
  cost_rate: number | null;
  gross_margin_rate: number | null;
  labor_rate: number | null;
  opex_rate: number | null;
  customer_count: number;
  avg_price_tax_excluded: number | null;
  thresholds: Record<string, ThresholdEntry>;
};

type TenantKpi = {
  sales_tax_excluded: number;
  cost_rate: number | null;
  gross_margin_rate: number | null;
  labor_rate: number | null;
  opex_rate: number | null;
  customer_count: number;
  visitor_count_method: 'menu_derive' | 'manual_input' | 'external_sync' | null;
  avg_price_tax_excluded: number | null;
};

type OverviewResponse = {
  period: { start: string; end: string; label: string };
  tenant_kpi: TenantKpi;
  stores: StoreKpi[];
  prev_month?: { label: string; tenant_kpi: TenantKpi; stores: StoreKpi[] } | null;
  prev_year?: { label: string; tenant_kpi: TenantKpi; stores: StoreKpi[] } | null;
  publish_gate?: PublishGateInfo;
};

// v0.5⑤ (②目標達成率インライン): KpiTargetProgressPage.tsx が使う /kpi-targets/progress の
// 応答のうち、この画面で使う部分だけを最小限に型定義する (全体は KpiTargetProgressPage.tsx 参照)。
type TargetPeriodAgg = {
  target: number | null;
  progress_rate: number | null;
  to_date: { months: string[]; target: number | null; actual: number | null; achievement_rate: number | null; variance: number | null };
};
type TargetProgressResponse = {
  year: TargetPeriodAgg;
  months: { month: string; target: number | null; target_incomplete: boolean }[];
  target_basis: 'own' | 'children_sum' | 'none';
  rollup_uncovered: { total: number };
};

// v0.5⑤ (①統合画面レイアウト、docs/design/v05-dashboard-consolidation-detail.md §1-1):
// TrendsPage.tsx と同じ /dashboard/trends を再利用し、直近6ヶ月の売上推移をこの画面にも埋め込む。
type TrendRowMini = { period: string; total_sales_tax_excluded: number };
type TrendsResponseMini = { series: TrendRowMini[] };

/** N ヶ月前 (0=当月) の月初日。TrendsPage.tsx の monthsBackStart と同型だが、基準日を
 *  「今日」ではなくこの画面が見ている periodStart にする (固定中の月と食い違わせないため)。*/
const monthsBeforePeriod = (periodStart: string, n: number): string => {
  const [y, m] = periodStart.split('-').map(Number);
  const d = new Date(y!, m! - 1 - n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const buildMonthOptions = (): string[] => {
  const opts: string[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  }
  return opts;
};

export function DashboardOverviewPage() {
  const rootLabel = useRootLabel();
  // v0.3 (S-11 DL権限管理): export:view権限が無いユーザーにはCSV出力ボタンを非表示にする
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canExport = hasPermissionForFeatureAction('export', 'view');
  // NodeSwitcher で選択中のスコープ。null (=ルート/全社) の場合は従来通り /dashboard/overview を、
  // 選択中の場合は選択ノード配下に絞った /dashboard/nodes/:nodeId/overview を呼ぶ
  // (S-9 QA/Design レビュー指摘: NodeSwitcher の選択がどの画面にも反映されていなかった問題への対応)
  const selectedNodeId = useScopeStore((s) => s.selectedNodeId);
  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-4): 固定中は範囲の終了月を
  // 「今見ている月」として単一月に採用する
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);
  const monthOptions = buildMonthOptions();
  // 固定した月が生成レンジ(直近24ヶ月)より古い場合に<select>のvalueが一覧に無く表示が崩れるのを防ぐ
  if (isPinned && pinnedPeriodEnd && !monthOptions.includes(pinnedPeriodEnd)) {
    monthOptions.push(pinnedPeriodEnd);
    monthOptions.sort().reverse();
  }
  const [periodStart, setPeriodStart] = useState(() => (isPinned && pinnedPeriodEnd ? pinnedPeriodEnd : monthOptions[0]!));
  // §0-2-2: storeの復元(loadForTenant)が本ページの初回マウントより後に完了する場合があるため、
  // useState初期化子だけに頼らずisPinned/pinnedPeriodEndの変化を監視して補正する。ページ内で
  // ユーザーが手動変更した場合はこのeffectの依存配列が変わらないため上書きされない。
  useEffect(() => {
    if (isPinned && pinnedPeriodEnd) setPeriodStart(pinnedPeriodEnd);
  }, [isPinned, pinnedPeriodEnd]);
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<OverviewResponse>({
    queryKey: ['dashboard', 'overview', selectedNodeId, periodStart, 'compare=prev_month'],
    queryFn: () => apiFetch<OverviewResponse>(
      selectedNodeId
        ? `/dashboard/nodes/${selectedNodeId}/overview?period_start=${periodStart}&compare=prev_month`
        : `/dashboard/overview?period_start=${periodStart}&compare=prev_month`,
    ),
  });

  // ⚠️ 見出しの表記: GET /nodes は権限で絞り込まれていない組織ツリーを返すため、rootLabel
  // (ツリーのルートnode種別ラベル、例:「会社」) をown_store/node_scope等の絞られたユーザーにも
  // そのまま出すと、実際に/dashboard/overviewが返す(絞り込み済みの)データ範囲と食い違い
  // 誤解を生む(§8-182と同型: 未スコープの組織ツリー vs スコープ済みデータの不一致)。
  // ノードを明示選択中ならそのノード名、未選択(既定表示)なら dashboard:view がテナント全体
  // スコープかどうかで判定し、絞られているなら実際に返ってきたstoresから見出しを組み立て直す。
  const roots = useScopeStore((s) => s.roots);
  const authUser = useAuthStore((s) => s.user);
  const selectedNode = selectedNodeId ? findNodeInTree(roots, selectedNodeId) : null;
  const isTenantWideDashboard = hasTenantWidePermission(
    authUser?.effective_permissions, 'dashboard', 'view', authUser?.non_inheriting_features,
  );
  const headerScopeLabel = selectedNode
    ? selectedNode.name
    : isTenantWideDashboard
      ? rootLabel
      : data && data.stores.length === 1
        ? data.stores[0]!.store_name
        : null;

  // v0.4 追加要件⑭ 増分⑤ (D-466③): 選択中の月の売上目標に対する達成率をカードに添える。
  // ⚠️ 分子は /kpi-targets/progress を使わずこの画面が既に表示している tenant_kpi.sales_tax_excluded を
  //    そのまま使う。こうすると「カードの数値と達成率の分子が食い違う」余地が構造的に無くなる (D-459)。
  // ⚠️ D-944: 分母は /progress の該当月の目標 (自前の目標が無ければ配下の合計、D-936)。
  //    分母・表示可否の判定は resolveMonthTargetCard に集約している (KPIカードの「目標比」も同じ値を使う)。
  // ⚠️ 目標が登録されていないノードでは何も表示しない (§6-0 原則3)。
  const canViewTarget = hasPermissionForFeatureAction('kpi_target', 'view');
  // ノード未選択 (全社) のときの比較対象になるルートノード
  const { data: nodesForTarget } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'), enabled: canViewTarget,
  });
  const rootNodeId = nodesForTarget?.roots[0]?.node_id ?? null;
  const targetScopeNodeId = selectedNodeId ?? rootNodeId;
  const { data: tenantSettingsForTarget } = useQuery<{ fiscal_year_start_month?: number }>({
    queryKey: ['tenant-settings'],
    queryFn: () => apiFetch<{ fiscal_year_start_month?: number }>('/tenant-settings'),
    enabled: canViewTarget,
  });
  const fiscalYearStartMonth = tenantSettingsForTarget?.fiscal_year_start_month ?? 4;
  const resolvedFiscalYear = fiscalPositionOfMonth(periodStart, fiscalYearStartMonth).fiscalYear;
  const { data: progressData } = useQuery<TargetProgressResponse>({
    queryKey: ['kpi-target-progress', targetScopeNodeId, resolvedFiscalYear],
    queryFn: () => apiFetch<TargetProgressResponse>(
      `/kpi-targets/progress?fiscal_year=${resolvedFiscalYear}&node_id=${encodeURIComponent(targetScopeNodeId!)}`),
    enabled: canViewTarget && !!targetScopeNodeId,
  });
  const monthTargetCard = resolveMonthTargetCard({
    month: periodStart,
    months: progressData?.months,
    sales: data?.tenant_kpi.sales_tax_excluded,
    // ⚠️ 分子と同じゲート結果で判定する (この画面は Full 専用。Lite は LiteDashboardPage)
    excludedStoreCount: data?.publish_gate?.excluded_store_ids.length ?? 0,
  });
  const salesTarget = monthTargetCard.kind === 'hidden' ? null : monthTargetCard.target;
  const salesTargetRate = monthTargetCard.kind === 'rate' ? monthTargetCard.rate : null;
  const yearPaceId = useId();
  const yearPace = progressData && progressData.year.target && progressData.year.to_date.target !== null
    ? progressData.year.to_date.target / progressData.year.target : null;

  // v0.5⑤ (①画面集約): 推移グラフ (直近6ヶ月・売上) をこの画面に埋め込む。TrendsPage.tsx と
  // 同じエンドポイントを再利用するため新規バックエンド実装は不要。
  const { data: trendsData } = useQuery<TrendsResponseMini>({
    queryKey: ['dashboard-trends-inline', selectedNodeId, periodStart],
    queryFn: () => apiFetch<TrendsResponseMini>(
      `/dashboard/trends?period_start=${monthsBeforePeriod(periodStart, 5)}&period_end=${periodStart}` +
      (selectedNodeId ? `&store_id=${selectedNodeId}` : ''),
    ),
  });

  const stores = data?.stores ?? [];
  const prevMap = useMemo(() => {
    const m = new Map<string, StoreKpi>();
    (data?.prev_month?.stores ?? []).forEach((s) => m.set(s.store_id, s));
    return m;
  }, [data?.prev_month]);

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort(
    stores as unknown as Array<Record<string, unknown>>,
    { defaultKey: 'store_name', defaultDir: 'asc' },
  );

  /** 指定 kpi_type で閾値超過している店舗が1件でもあれば、その direction (above/below) に応じて
   *  「閾値超過」/「閾値未達」を動的に組み立てる。閾値の direction はマスタ画面でユーザーが自由に
   *  設定できるため、文言を固定してはいけない (QA/Design レビュー指摘、direction 無視で誤表示するバグを修正) */
  const alertLabel = (kpiType: string): string | undefined => {
    const hit = stores.find((s) => s.thresholds[kpiType]?.alert);
    if (!hit) return undefined;
    return hit.thresholds[kpiType]!.direction === 'above' ? '閾値超過店舗があります' : '閾値未達店舗があります';
  };
  const prevTenant = data?.prev_month?.tenant_kpi ?? null;

  // v0.5⑤ (2026-09-08 userフィードバック §1-3): 店舗別サマリの行クリックで画面遷移せずその場に
  // PL内訳を展開する。1件ずつ開くアコーディオン (モック #store-accordion と同じ挙動)。
  const [expandedStoreId, setExpandedStoreId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-h1">経営ダッシュボード{headerScopeLabel ? `（${headerScopeLabel}）` : ''}</h1>
        <div className="flex items-center gap-2">
          <select
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            className="px-3 py-1.5 rounded border border-neutral-line text-sm focus-ring"
            aria-label="対象月"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {m.slice(0, 4)}年{Number(m.slice(5, 7))}月
              </option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-neutral-line text-sm hover:bg-neutral-panel disabled:opacity-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            更新
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="text-neutral-sub">読み込み中…</div>
      )}
      {isError && (
        <div className="p-4 rounded bg-warning-bg text-warning-text border border-warning-border">
          データ取得に失敗しました：{(error as Error).message}
        </div>
      )}

      {data && (
        <>
          <PublishGateBanner publishGate={data.publish_gate} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <KpiCard
              title="売上高 (税抜)"
              value={data.tenant_kpi.sales_tax_excluded}
              format="currency"
              subtitle={data.period.label}
              // ⚠️ subtitle ではなく footnote を使うこと。subtitle は `!alert && !delta` の
              //    ときしか描画されず、この画面は前月比 (delta) を常に渡すため一度も出ない。
              footnote={salesTargetRate != null ? `目標比 ${formatPercent(salesTargetRate)}` : undefined}
              alertMessage={alertLabel('sales')}
              delta={prevTenant ? {
                prevValue: prevTenant.sales_tax_excluded,
                label: `前月比 (${data.prev_month?.label})`,
              } : null}
            />
            <KpiCard
              title="粗利率"
              value={data.tenant_kpi.gross_margin_rate}
              format="percent"
              alertMessage={alertLabel('gross_margin')}
              delta={prevTenant ? {
                prevValue: prevTenant.gross_margin_rate,
                label: '前月比',
              } : null}
            />
            <KpiCard
              title="原価率"
              value={data.tenant_kpi.cost_rate}
              format="percent"
              alertMessage={alertLabel('cost_rate')}
              delta={prevTenant ? {
                prevValue: prevTenant.cost_rate,
                label: '前月比',
                invert: true,
              } : null}
            />
            <KpiCard
              title="人件費率"
              value={data.tenant_kpi.labor_rate}
              format="percent"
              alertMessage={alertLabel('labor_rate')}
              delta={prevTenant ? {
                prevValue: prevTenant.labor_rate,
                label: '前月比',
                invert: true,
              } : null}
            />
            <KpiCard
              title="諸経費率"
              value={data.tenant_kpi.opex_rate}
              format="percent"
              alertMessage={alertLabel('opex_rate')}
              delta={prevTenant ? {
                prevValue: prevTenant.opex_rate,
                label: '前月比',
                invert: true,
              } : null}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <KpiCard
              title="来客数"
              value={data.tenant_kpi.customer_count}
              format="count"
              unit=" 人"
              alertMessage={alertLabel('customer_count')}
              sourceBadge={visitorCountSourceBadge(data.tenant_kpi.visitor_count_method)}
              delta={prevTenant ? {
                prevValue: prevTenant.customer_count,
                label: '前月比',
              } : null}
            />
            <KpiCard
              title="平均単価 (税抜、店内)"
              value={data.tenant_kpi.avg_price_tax_excluded}
              format="currency"
              alertMessage={alertLabel('avg_price')}
              delta={prevTenant ? {
                prevValue: prevTenant.avg_price_tax_excluded,
                label: '前月比',
              } : null}
            />
          </div>

          {canViewTarget && (monthTargetCard.kind !== 'hidden' || progressData?.year.target != null) && (
            <section>
              <h2 className="text-h2 mb-3">目標達成率</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {monthTargetCard.kind !== 'hidden' && (
                  <div className="border border-neutral-line rounded-lg bg-white p-4">
                    <div className="text-xs text-neutral-sub font-semibold">当月 目標達成率</div>
                    {monthTargetCard.kind === 'notice' ? (
                      <p className="mt-1 text-sm text-neutral-sub">{monthTargetCard.message}</p>
                    ) : (
                      <>
                        <div className="mt-1 text-2xl font-extrabold tabular-nums">{formatPercent(salesTargetRate)}</div>
                        <div
                          role="progressbar" aria-label="当月の目標に対する達成率" aria-valuemin={0}
                          aria-valuemax={Math.max(100, Math.round((salesTargetRate ?? 0) * 1000) / 10)}
                          aria-valuenow={salesTargetRate == null ? undefined : Math.round(salesTargetRate * 1000) / 10}
                          className="mt-2 h-2 rounded-full bg-neutral-line overflow-hidden"
                        >
                          <div className="h-full bg-chart-2" style={{ width: `${Math.min(100, (salesTargetRate ?? 0) * 100)}%` }} />
                        </div>
                      </>
                    )}
                    <div className="mt-2 flex justify-between text-xs text-neutral-sub">
                      <span>実績 <span className="tabular-nums font-medium text-neutral-text">{formatCurrency(data?.tenant_kpi.sales_tax_excluded)}</span></span>
                      <span>目標 <span className="tabular-nums font-medium text-neutral-text">{formatCurrency(salesTarget)}</span></span>
                    </div>
                  </div>
                )}
                {progressData?.year.target != null && (
                  <div className="border border-neutral-line rounded-lg bg-white p-4">
                    <div className="text-xs text-neutral-sub font-semibold">年度累計</div>
                    <div className="mt-0.5 text-xs text-neutral-sub">{formatCountedMonths(progressData.year.to_date.months)}</div>
                    <div className="mt-1 text-2xl font-extrabold tabular-nums">
                      {formatPercent(progressData.year.to_date.achievement_rate)}
                      <span className="ml-1 text-xs font-normal text-neutral-sub">達成率（経過分）</span>
                    </div>
                    {/* バー = 進捗率 (年度の目標に対する実績)。▲ = ここまでの目標。⚠️ 未達でも赤を使わない */}
                    <div
                      role="progressbar" aria-label="年度目標に対する進捗率" aria-valuemin={0}
                      aria-valuemax={Math.max(100, Math.round((progressData.year.progress_rate ?? 0) * 1000) / 10)}
                      aria-valuenow={progressData.year.progress_rate == null ? undefined : Math.round(progressData.year.progress_rate * 1000) / 10}
                      aria-describedby={yearPace !== null ? yearPaceId : undefined}
                      className="relative mt-3 h-2 rounded-full bg-neutral-line"
                    >
                      <div className="h-full rounded-full bg-chart-2" style={{ width: `${Math.max(0, Math.min(100, (progressData.year.progress_rate ?? 0) * 100))}%` }} />
                      {yearPace !== null && (
                        <span aria-hidden="true" className="absolute top-full -translate-x-1/2 text-[10px] leading-none text-neutral-text"
                          style={{ left: `${Math.max(0, Math.min(100, yearPace * 100))}%` }}>▲</span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs text-neutral-sub">
                      <span>進捗率 <span className="tabular-nums font-medium text-neutral-text">{formatPercent(progressData.year.progress_rate)}</span></span>
                      {yearPace !== null && <span id={yearPaceId}>▲ ここまでの目標 <span className="tabular-nums">{formatPercent(yearPace)}</span></span>}
                    </div>
                    <div className="mt-2 flex flex-wrap justify-between gap-x-3 text-xs text-neutral-sub">
                      <span>乖離 <span className="tabular-nums font-medium text-neutral-text">{formatVariance(progressData.year.to_date.variance)}</span></span>
                      <span>
                        {progressData.target_basis === 'children_sum' ? '配下の目標合計' : '年度の目標'}{' '}
                        <span className="tabular-nums font-medium text-neutral-text">{formatCurrency(progressData.year.target)}</span>
                      </span>
                    </div>
                    {progressData.rollup_uncovered.total > 0 && (
                      <p className="mt-2 text-xs text-neutral-sub">
                        目標が一部未登録の組織があります。<Link to="/dashboard/targets" className="inline-flex min-h-[44px] items-center underline">目標と実績</Link>で確認できます。
                      </p>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {stores.length > 0 && (
            <section>
              <h2 className="text-h2 mb-3">店舗・デリバリー内訳（税抜）</h2>
              <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
                {stores.map((s) => {
                  const total = s.store_sales_tax_excluded + s.delivery_sales_tax_excluded;
                  const storePct = total > 0 ? (s.store_sales_tax_excluded / total) * 100 : 0;
                  const deliveryPct = total > 0 ? 100 - storePct : 0;
                  return (
                    <div key={s.store_id}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium">{s.store_name}</span>
                        <span className="tabular-nums text-neutral-sub">{formatCurrency(total)}</span>
                      </div>
                      <div className="flex h-4 rounded overflow-hidden bg-neutral-line">
                        <div style={{ width: `${storePct}%` }} className="bg-chart-1" title={`店舗売上 ${formatCurrency(s.store_sales_tax_excluded)}`} />
                        <div style={{ width: `${deliveryPct}%` }} className="bg-chart-3" title={`デリバリー売上 ${formatCurrency(s.delivery_sales_tax_excluded)}`} />
                      </div>
                      {/* userフィードバック(2026-09-08本番実機): タップできないモバイルではtitle属性の
                          金額が読めず「内訳と言いながら数字が出ていない」状態だった。バー直下に明示表示する */}
                      <div className="flex items-center justify-between text-xs text-neutral-sub mt-1 tabular-nums">
                        <span>店舗 {formatCurrency(s.store_sales_tax_excluded)}（{storePct.toFixed(0)}%）</span>
                        <span>デリバリー {formatCurrency(s.delivery_sales_tax_excluded)}（{deliveryPct.toFixed(0)}%）</span>
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center gap-4 text-xs text-neutral-sub pt-1">
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-chart-1" aria-hidden />店舗売上</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-chart-3" aria-hidden />デリバリー売上</span>
                </div>
              </div>
            </section>
          )}

          {(trendsData?.series.length ?? 0) > 1 && (
            <section>
              <h2 className="text-h2 mb-3">推移（直近6ヶ月・売上）</h2>
              <div className="border border-neutral-line rounded-lg bg-white p-4" style={{ height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={trendsData!.series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tickFormatter={(p: string) => p.slice(0, 7)} fontSize={12} />
                    <YAxis tickFormatter={(v: number) => formatCurrency(v)} fontSize={12} width={90} />
                    <Tooltip formatter={(v: number) => formatCurrency(v)} labelFormatter={(p: string) => p.slice(0, 7)} />
                    <Line type="monotone" dataKey="total_sales_tax_excluded" name="売上" stroke="#0B2450" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          {selectedNodeId && <AiPeriodCommentPanel nodeId={selectedNodeId} targetMonth={periodStart.slice(0, 7)} />}

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-h2">店舗別サマリ</h2>
              {canExport && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm text-brand-red hover:underline"
                  onClick={() => {
                    const headers = ['store_id', 'store_name', 'sales_tax_excluded', 'cost_rate', 'gross_margin_rate', 'labor_rate', 'opex_rate', 'customer_count', 'avg_price_tax_excluded'];
                    downloadAsCsv(`dashboard-overview_${periodStart.slice(0, 7)}.csv`, headers, stores);
                  }}>
                  <Download size={14} />
                  CSV出力
                </button>
              )}
            </div>
            <div className="overflow-x-auto border border-neutral-line rounded-lg bg-white">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-neutral-panel text-neutral-sub">
                  <tr>
                    <SortableTh sortKey="store_name" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="sticky left-0 z-20 bg-neutral-panel shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] whitespace-nowrap">店舗名</SortableTh>
                    <SortableTh sortKey="sales_tax_excluded" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="whitespace-nowrap">売上（税抜）</SortableTh>
                    <SortableTh sortKey="gross_margin_rate" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="whitespace-nowrap">粗利率</SortableTh>
                    <SortableTh sortKey="cost_rate" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="whitespace-nowrap">原価率</SortableTh>
                    <SortableTh sortKey="labor_rate" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="whitespace-nowrap">人件費率</SortableTh>
                    <SortableTh sortKey="opex_rate" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="whitespace-nowrap">諸経費率</SortableTh>
                    <SortableTh sortKey="customer_count" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="whitespace-nowrap">来客数</SortableTh>
                    <SortableTh sortKey="avg_price_tax_excluded" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="whitespace-nowrap">平均単価</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {(sortedRows as unknown as StoreKpi[]).map((s) => {
                    const prev = prevMap.get(s.store_id);
                    const salesDelta = prev ? s.sales_tax_excluded - prev.sales_tax_excluded : null;
                    const costRateAlert = s.thresholds.cost_rate?.alert ?? false;
                    const grossMarginAlert = s.thresholds.gross_margin?.alert ?? false;
                    const laborRateAlert = s.thresholds.labor_rate?.alert ?? false;
                    const opexRateAlert = s.thresholds.opex_rate?.alert ?? false;
                    const salesAlert = s.thresholds.sales?.alert ?? false;
                    const customerCountAlert = s.thresholds.customer_count?.alert ?? false;
                    const avgPriceAlert = s.thresholds.avg_price?.alert ?? false;
                    const rowHasAlert =
                      costRateAlert || grossMarginAlert || laborRateAlert || opexRateAlert || salesAlert || customerCountAlert || avgPriceAlert;
                    const isExpanded = expandedStoreId === s.store_id;
                    return (
                      <Fragment key={s.store_id}>
                      <tr
                        className={`group border-t border-neutral-line ${
                          rowHasAlert ? 'bg-warning-row' : ''
                        } ${isExpanded ? 'bg-group-dashboard-bg' : ''}`}
                      >
                        {/* userフィードバック(2026-09-08本番実機、iPhone Safari): tr全体のonClickだと
                            横スクロールする幅広テーブル内でタップがスクロール操作と誤認され展開できない
                            ことがあった。sticky先頭セル内の明示的なbuttonをタップ対象にすることで、
                            スクロール位置に関わらず常に画面内にある確実なタップ領域にする */}
                        <td className={`sticky left-0 z-10 p-0 whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] ${rowHasAlert ? 'bg-warning-row' : isExpanded ? 'bg-group-dashboard-bg' : 'bg-white group-hover:bg-neutral-panel'}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedStoreId(isExpanded ? null : s.store_id)}
                            className="w-full flex items-center gap-1 px-3 py-3 min-h-[44px] font-medium text-left"
                          >
                            {isExpanded ? <ChevronDown size={14} className="text-neutral-sub shrink-0" aria-hidden /> : <ChevronRight size={14} className="text-neutral-sub shrink-0" aria-hidden />}
                            {s.store_name}
                          </button>
                        </td>
                        <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap ${salesAlert ? 'text-warning-text font-semibold' : ''}`}>
                          <div>
                            {formatCurrency(s.sales_tax_excluded)}
                            {salesAlert && <span className="ml-1">⚠</span>}
                          </div>
                          {salesDelta != null && (
                            <div className={`text-xs ${salesDelta > 0 ? 'text-success-text' : salesDelta < 0 ? 'text-warning-text' : 'text-neutral-sub'}`}>
                              {salesDelta > 0 ? '+' : salesDelta < 0 ? '−' : '±'}¥{Math.abs(salesDelta).toLocaleString('ja-JP')}
                            </div>
                          )}
                        </td>
                        <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap ${grossMarginAlert ? 'text-warning-text font-semibold' : ''}`}>
                          {formatPercent(s.gross_margin_rate)}
                          {grossMarginAlert && <span className="ml-1">⚠</span>}
                        </td>
                        <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap ${costRateAlert ? 'text-warning-text font-semibold' : ''}`}>
                          {formatPercent(s.cost_rate)}
                          {costRateAlert && <span className="ml-1">⚠</span>}
                        </td>
                        <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap ${laborRateAlert ? 'text-warning-text font-semibold' : ''}`}>
                          {formatPercent(s.labor_rate)}
                          {laborRateAlert && <span className="ml-1">⚠</span>}
                        </td>
                        <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap ${opexRateAlert ? 'text-warning-text font-semibold' : ''}`}>
                          {formatPercent(s.opex_rate)}
                          {opexRateAlert && <span className="ml-1">⚠</span>}
                        </td>
                        <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap ${customerCountAlert ? 'text-warning-text font-semibold' : ''}`}>
                          {formatCount(s.customer_count)}
                          {customerCountAlert && <span className="ml-1">⚠</span>}
                        </td>
                        <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap ${avgPriceAlert ? 'text-warning-text font-semibold' : ''}`}>
                          {formatCurrency(s.avg_price_tax_excluded)}
                          {avgPriceAlert && <span className="ml-1">⚠</span>}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-t border-neutral-line">
                          <td colSpan={8} className="bg-neutral-panel p-0">
                            <StoreDrillDown storeId={s.store_id} storeName={s.store_name} periodStart={periodStart} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-neutral-sub mt-2">※ 店舗名タップでPL内訳をその場に展開 / 見出しクリックで並び替え</p>
          </section>
        </>
      )}
    </div>
  );
}

// ============================================================================
// v0.5⑤ (2026-09-08 userフィードバック §1-3): 店舗別サマリの行を展開した時のインライン・
// ドリルダウン。既存の /pl-tree (PlTreePage.tsx と同じAPI) と新設 /supplier-costs
// (platform-reviewer MEDIUM-1、ingredient別の /cost-items と同型・vendor_name版) を
// 遅延フェッチする (行を開いた時だけ、初期表示では叩かない)。
// ============================================================================
type DrillDownCogsItem = { ingredient_name: string; amount: number; ratio: number | null };
type DrillDownCogsCategory = { category_name: string; total: number; ratio: number | null; items: DrillDownCogsItem[] };
type DrillDownOpexItem = { item_name: string; amount: number; ratio: number | null };
type PlTreeDrillDownResponse = {
  cost: {
    cogs: { total: number; categories: DrillDownCogsCategory[] };
    labor: { total: number };
    opex: { total: number; items: DrillDownOpexItem[] };
  };
};
type SupplierCostRow = { vendor_name: string; total_cost_tax_excluded: number };
type SupplierCostsResponse = { items: SupplierCostRow[] };

function StoreDrillDown({ storeId, storeName, periodStart }: { storeId: string; storeName: string; periodStart: string }) {
  const { data: plTree, isLoading: plLoading } = useQuery({
    queryKey: ['dashboard', 'pl-tree', storeId, periodStart],
    queryFn: () => apiFetch<PlTreeDrillDownResponse>(`/dashboard/stores/${storeId}/pl-tree?period_start=${periodStart}`),
  });
  const { data: supplierCosts, isLoading: supplierLoading } = useQuery({
    queryKey: ['dashboard', 'supplier-costs', storeId, periodStart],
    queryFn: () => apiFetch<SupplierCostsResponse>(`/dashboard/stores/${storeId}/supplier-costs?period_start=${periodStart}`),
  });

  const cogsItems = (plTree?.cost.cogs.categories ?? []).flatMap((cat) => cat.items);

  return (
    <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div>
        <h4 className="text-xs font-semibold text-neutral-sub mb-2">勘定科目別内訳</h4>
        {plLoading ? <p className="text-xs text-neutral-sub">読み込み中…</p> : (
          <table className="w-full text-sm">
            <tbody>
              {cogsItems.length === 0 && plTree && (
                <tr><td className="py-1 text-neutral-sub text-xs">データがありません</td></tr>
              )}
              {cogsItems.map((item, i) => (
                <tr key={i} className="border-t border-neutral-line first:border-t-0">
                  <td className="py-1.5 pr-2 truncate max-w-[10rem]" title={item.ingredient_name}>{item.ingredient_name}</td>
                  <td className="py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(item.amount)}</td>
                </tr>
              ))}
              {plTree && (
                <>
                  <tr className="border-t border-neutral-line"><td className="py-1.5">人件費</td><td className="py-1.5 text-right tabular-nums">{formatCurrency(plTree.cost.labor.total)}</td></tr>
                  {plTree.cost.opex.items.map((item, i) => (
                    <tr key={`opex-${i}`} className="border-t border-neutral-line">
                      <td className="py-1.5 pr-2 truncate max-w-[10rem]" title={item.item_name}>{item.item_name}</td>
                      <td className="py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <h4 className="text-xs font-semibold text-neutral-sub mb-2">仕入先別コスト</h4>
        {supplierLoading ? <p className="text-xs text-neutral-sub">読み込み中…</p> : (
          <table className="w-full text-sm">
            <tbody>
              {(supplierCosts?.items.length ?? 0) === 0
                ? <tr><td className="py-1 text-neutral-sub text-xs">データがありません</td></tr>
                : supplierCosts!.items.map((s, i) => (
                  <tr key={i} className="border-t border-neutral-line first:border-t-0">
                    <td className="py-1.5 pr-2 truncate max-w-[10rem]" title={s.vendor_name}>{s.vendor_name}</td>
                    <td className="py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(s.total_cost_tax_excluded)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
      {/* ⚠️ この panel は min-w-[860px] のテーブルの td 内にあるため、行の幅は常に 860px ある。
          justify-end にすると lg 未満で リンクが x=664〜845 に置かれ、390px 幅の端末では
          横スクロールしない限り画面外になり「リンクが出てこない」ことになる (2026-09-10 本番実機報告)。
          order-first は、mobile では内訳テーブル数十行の下に埋もれて到達できないため */}
      <div className="flex items-start justify-start order-first lg:order-none">
        <Link to={`/dashboard/stores/${storeId}?period_start=${periodStart}`}
          className="inline-flex items-center gap-1 text-sm text-brand-red hover:underline min-h-[44px]">
          {storeName}の詳細ページを開く <ArrowUpRight size={14} aria-hidden />
        </Link>
      </div>
    </div>
  );
}
